import React, { useEffect, useRef, useState } from "react";

import { LiveWidgetTypes } from "@data-types/live-widget/LiveWidgetTypes";
import { WidgetLocation } from "@data-types/live-widget/WidgetLocation";
import {
	LiveStreamConfigMobile,
	LiveStreamConfigWeb,
	LiveWidgetContainer,
	isTablet,
} from "@features/live-widgets/shared";
import { ProviderNameEnum } from "@gp/models";
import { EventOffer, SportOffer } from "@gp/offer";
import { useLiveWidgetStore } from "@state/hooks/live-widgets";
import EventViewStore from "@state/stores/offer/event/EventViewStore.mobile";
import { HEADER_HEIGHT } from "@utils/style/constants";
import classNames from "classnames";
import { observer } from "mobx-react";

import { PinWidgetButton } from ".";

export const EventSimulationWidget = observer(
	function EventSimulationWidget(props: {
		event: EventOffer;
		sport: SportOffer;
		eventViewStore: EventViewStore;
	}) {
		const liveWidgetStore = useLiveWidgetStore();
		const { findActiveWidget } = liveWidgetStore;
		const activeWidget = findActiveWidget(
			props.event.id,
			WidgetLocation.EVENT_OFFER
		);
		const playerConfiguration = isTablet()
			? LiveStreamConfigWeb
			: LiveStreamConfigMobile;

		const widgetRef = useRef<HTMLDivElement | null>(null);
		const eventMessageRef = useRef<HTMLDivElement | null>(null);
		const resizeTimeoutId = useRef<null | ReturnType<typeof setTimeout>>(
			null
		);

		const [emptyHeight, setEmptyHeight] = useState(0);
		const [displayClass, setDisplayClass] = useState("");
		const headerHeight = useRef(HEADER_HEIGHT);

		useEffect(() => {
			if (activeWidget?.isPinned === false) {
				setDisplayClass("block");
				return;
			}

			const updateHeight = () => {
				if (widgetRef.current && eventMessageRef.current) {
					headerHeight.current =
						widgetRef.current?.getBoundingClientRect().top;

					const widgetHeight =
						widgetRef.current.offsetHeight + headerHeight.current;

					const emptyMessageHeight =
						eventMessageRef.current.getBoundingClientRect().top;

					setEmptyHeight(widgetHeight - emptyMessageHeight);

					if (emptyHeight < 0) {
						setDisplayClass("none");
					}
				}
			};

			if (resizeTimeoutId.current != null) {
				clearTimeout(resizeTimeoutId.current);
			}

			resizeTimeoutId.current = setTimeout(() => {
				updateHeight();
			}, 800);

			window.addEventListener("resize", updateHeight);

			return () => {
				window.removeEventListener("resize", updateHeight);
			};
		}, [activeWidget]);

		useEffect(() => {
			const widget = liveWidgetStore.findActiveLmtWidget(
				props.event.id,
				WidgetLocation.EVENT_OFFER
			);

			if (
				widget?.provider === ProviderNameEnum.ExeFeed &&
				props.event.source?.matchId == null
			) {
				liveWidgetStore.handleExeFeedSourceRemoved(props.event.id);
			}
		}, [props.event.source?.matchId]);

		if (!activeWidget) {
			return null;
		}

		const emptyContainerStyle = {
			display: displayClass,
			...(emptyHeight > 0 && {
				minHeight: `${Math.min(emptyHeight, 225)}px`,
			}),
		};

		return (
			<>
				{activeWidget && (
					<>
						<div
							className={classNames("event__widget widget", {
								"is-pinned": activeWidget?.isPinned,
							})}
							ref={widgetRef}
						>
							<PinWidgetButton />

							<LiveWidgetContainer
								eventId={props.event.id}
								location={WidgetLocation.EVENT_OFFER}
								lmtProps={{
									divProps: {
										className: classNames(
											"widget--stream widget--lmt",
											{
												"widget--betradar":
													activeWidget?.provider ===
													ProviderNameEnum.BetRadar,
												"widget--statscore":
													activeWidget?.provider ===
														ProviderNameEnum.LSports &&
													!activeWidget?.isError,
												"widget--exe":
													activeWidget?.provider ===
														ProviderNameEnum.ExeFeed &&
													!activeWidget?.isError,
												"is-pinned":
													activeWidget?.isPinned,
											}
										),
									},
								}}
								lsProps={{
									divProps: {
										className: classNames(
											"widget--stream widget--ls",
											{
												"is-pinned":
													activeWidget?.isPinned,
											}
										),
									},
									config: playerConfiguration,
								}}
							/>
						</div>
						{activeWidget?.isPinned &&
							activeWidget.type === "LMT" && (
								<div
									ref={eventMessageRef}
									style={emptyContainerStyle}
									className={classNames(
										"event__message event__message--lmt",
										{
											"event__message--betradar":
												props.event.source
													?.providerName ===
												ProviderNameEnum.BetRadar,
											"event__message--statscore":
												props.event.source
													?.providerName ===
												ProviderNameEnum.LSports,
											"event__message--exe":
												props.event.source
													?.providerName ===
												ProviderNameEnum.ExeFeed,
										}
									)}
								/>
							)}

						{activeWidget?.isPinned &&
							activeWidget.type === LiveWidgetTypes.LS && (
								<div
									className="event__message event__message--ls"
									style={emptyContainerStyle}
									ref={eventMessageRef}
								/>
							)}
					</>
				)}
			</>
		);
	}
);
